<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
        <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <span class="text-3xl">🎯</span>
              Dashboard Ejecutivo de Producción e Instalación
            </h1>
            <p class="text-sm text-gray-600 mt-2">
              Análisis en tiempo real de producción, instalación y eficiencia operativa
            </p>
          </div>
          
          <!-- Filtros -->
          <div class="w-full lg:w-auto flex flex-col sm:flex-row gap-3">
            <div class="flex flex-col sm:flex-row gap-2">
              <input 
                type="date" 
                v-model="fechaInicio" 
                class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Fecha Inicio"
              >
              <input 
                type="date" 
                v-model="fechaFin" 
                class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Fecha Fin"
              >
            </div>
            <div class="flex gap-2">
              <label class="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input type="checkbox" v-model="comparar" class="rounded text-blue-600 focus:ring-2 focus:ring-blue-500">
                <span class="text-sm text-gray-700">Comparar</span>
              </label>
              <button 
                @click="cargarDatos" 
                :disabled="loading"
                class="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                <span v-if="loading">⏳</span>
                <span v-else>🔍</span>
                {{ loading ? 'Cargando...' : 'Consultar' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p class="mt-4 text-gray-600 font-medium">Cargando datos del dashboard...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 shadow-lg">
        <div class="flex items-center gap-3">
          <span class="text-3xl">⚠️</span>
          <div>
            <h3 class="text-lg font-semibold text-red-800">Error al cargar los datos</h3>
            <p class="text-red-600 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Dashboard Content -->
      <div v-if="!loading && !error && dashboardData" class="space-y-6">
        <!-- Metadata Info -->
        <MetadataCard :metadata="dashboardData.metadata" />

        <!-- Alertas Críticas (si existen) -->
        <AlertasCard 
          v-if="dashboardData.alertas_criticas && dashboardData.alertas_criticas.total_alertas > 0"
          :alertas="dashboardData.alertas_criticas"
        />

        <!-- KPIs Principales -->
        <KPIsSection :kpis="dashboardData.kpis_principales" />

        <!-- Embudo de Producción -->
        <EmbudoProduccion :embudo="dashboardData.embudo_produccion" />

        <!-- Métricas de Tiempo y Análisis de Vendedores -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MetricasTiempo :metricas="dashboardData.metricas_tiempo" />
          <AnalisisVendedores :analisis="dashboardData.analisis_vendedores" />
        </div>

        <!-- Productos Críticos -->
        <ProductosCriticos 
          v-if="dashboardData.productos_criticos && dashboardData.productos_criticos.total_criticos > 0"
          :productos="dashboardData.productos_criticos"
        />

        <!-- Eficiencia Diaria y Tendencias Semanales -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EficienciaDiaria :eficiencia="dashboardData.eficiencia_diaria" />
          <TendenciasSemanales :tendencias="dashboardData.tendencias_semanales" />
        </div>

        <!-- Cuellos de Botella y Ranking de Productos -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CuellosBottella :analisis="dashboardData.analisis_cuellos_botella" />
          <RankingProductos :ranking="dashboardData.ranking_productos" />
        </div>

        <!-- Proyecciones -->
        <Proyecciones 
          v-if="dashboardData.proyecciones && dashboardData.proyecciones.aplicable"
          :proyecciones="dashboardData.proyecciones"
        />

        <!-- Comparativas (si está habilitado) -->
        <Comparativas 
          v-if="comparar && dashboardData.comparativas"
          :comparativas="dashboardData.comparativas"
        />
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos';
import MetadataCard from './components/MetadataCard.vue';
import AlertasCard from './components/AlertasCard.vue';
import KPIsSection from './components/KPIsSection.vue';
import EmbudoProduccion from './components/EmbudoProduccion.vue';
import MetricasTiempo from './components/MetricasTiempo.vue';
import AnalisisVendedores from './components/AnalisisVendedores.vue';
import ProductosCriticos from './components/ProductosCriticos.vue';
import EficienciaDiaria from './components/EficienciaDiaria.vue';
import TendenciasSemanales from './components/TendenciasSemanales.vue';
import CuellosBottella from './components/CuellosBottella.vue';
import RankingProductos from './components/RankingProductos.vue';
import Proyecciones from './components/Proyecciones.vue';
import Comparativas from './components/Comparativas.vue';

export default {
  name: 'DashboardProduccionInstalacion',
  components: {
    MetadataCard,
    AlertasCard,
    KPIsSection,
    EmbudoProduccion,
    MetricasTiempo,
    AnalisisVendedores,
    ProductosCriticos,
    EficienciaDiaria,
    TendenciasSemanales,
    CuellosBottella,
    RankingProductos,
    Proyecciones,
    Comparativas
  },
  data() {
    return {
      fechaInicio: '',
      fechaFin: '',
      comparar: true,
      loading: false,
      error: null,
      dashboardData: null,
      refreshInterval: null
    };
  },
  mounted() {
    this.inicializarFechas();
    this.cargarDatos();
    this.iniciarAutoRefresh();
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    inicializarFechas() {
      const hoy = new Date();
      this.fechaFin = this.formatearFecha(hoy);
      
      // Inicio del mes actual
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      this.fechaInicio = this.formatearFecha(inicioMes);
    },
    formatearFecha(fecha) {
      const año = fecha.getFullYear();
      const mes = String(fecha.getMonth() + 1).padStart(2, '0');
      const dia = String(fecha.getDate()).padStart(2, '0');
      return `${año}-${mes}-${dia}`;
    },
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await reportesNuevos.DASHBOARD_PRODUCCION_INSTALACION({
          fechaInicio: this.fechaInicio,
          fechaFin: this.fechaFin,
          comparar: this.comparar
        });
        
        console.log('Respuesta completa del servidor:', response);
        
        // El servicio initmixin.formatrespuesta ya devuelve response.data directamente
        // No hay un nivel adicional de .data
        if (response && response.metadata) {
          this.dashboardData = response;
        } else if (response && response.error) {
          throw new Error(response.error);
        } else {
          throw new Error('No se recibieron datos del servidor');
        }
      } catch (err) {
        console.error('Error al cargar dashboard:', err);
        this.error = err.response?.data?.message || err.message || 'Error al cargar los datos';
      } finally {
        this.loading = false;
      }
    },
    iniciarAutoRefresh() {
      // Actualizar cada 5 minutos (300000 ms)
      this.refreshInterval = setInterval(() => {
        this.cargarDatos();
      }, 300000);
    }
  }
};
</script>

<style scoped>
/* Animaciones suaves */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
